/**
 * ORR-111 — Servicio de auto-delegación AFIP (Milestone 4).
 *
 * Combina dos canales:
 *  - POST a Netlify Function `afip-auto-delegar` (toma token + PIN como headers)
 *  - SELECT directo sobre tablas Supabase (`afip_servicios_delegados`,
 *    `afip_delegacion_progress`) con patrón inyección de cliente Supabase como
 *    primer parámetro.
 *
 * Multi-tenancy: filtramos por organizacion_id en app-layer (regla del proyecto).
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// ===========================================
// Tipos del contract API (mirror del handler)
// ===========================================

export type ServicioCodigo =
  | 'wsccomu'
  | 'wsmtxca'
  | 'sistema_registral'
  | 'monotributo'

export interface ServicioDef {
  codigo: ServicioCodigo
  nombre: string
  descripcion: string
}

/**
 * Definición de servicios mostrados en el dialog. El nombre se sincroniza con
 * `SERVICIOS_DEF` del handler `netlify/functions/utils/afip-playwright.ts`.
 */
export const SERVICIOS_AFIP_UI: Record<ServicioCodigo, ServicioDef> = {
  wsccomu: {
    codigo: 'wsccomu',
    nombre: 'Consulta y lectura de Comunicaciones de e-Ventanilla',
    descripcion: 'Casillero ARCA / e-Ventanilla — habilita la lectura del buzón electrónico.',
  },
  wsmtxca: {
    codigo: 'wsmtxca',
    nombre: 'Web Service de Comprobantes con detalle (wsmtxca)',
    descripcion:
      'Factura electrónica A y B con detalle de ítems (RG 2904): le permite al estudio emitir facturas del cliente por web service. Menta todavía no lo usa.',
  },
  sistema_registral: {
    codigo: 'sistema_registral',
    nombre: 'Sistema Registral',
    descripcion: 'Datos registrales del contribuyente (domicilio, actividades).',
  },
  monotributo: {
    codigo: 'monotributo',
    nombre: 'Monotributo',
    descripcion: 'Régimen Simplificado — consulta y gestión.',
  },
}

export interface IniciarAutoDelegacionInput {
  clienteId: string
  organizacionId: string
  servicios: ServicioCodigo[]
  guardarClave?: boolean
  consentimientoUrl?: string
  provider?: 'netlify' | 'browserless'
}

export interface ServicioFallido {
  servicio: ServicioCodigo
  error: string
}

/**
 * El handler es una Netlify Background Function: el cliente recibe 202
 * inmediatamente y la Lambda sigue corriendo. El frontend hace polling de
 * `afip_delegacion_progress` por `jobId`, que genera el propio cliente.
 */
export interface IniciarAutoDelegacionResult {
  status: 'queued'
  message: string
  jobId: string
}

export interface AbrirAfipDirectoResult {
  cuit: string
  clave: string
}

export type ServicioDelegado =
  Database['public']['Tables']['afip_servicios_delegados']['Row']
export type DelegacionProgress =
  Database['public']['Tables']['afip_delegacion_progress']['Row']

const ENDPOINT_AUTO_DELEGAR =
  '/.netlify/functions/afip-auto-delegar-background'

const ENDPOINT_VALIDAR_AUTO_DELEGACION =
  '/.netlify/functions/afip-auto-delegar-validar'

const ENDPOINT_REPRESENTANTE = '/.netlify/functions/afip-representante'

/** Throws an `Error` with the function's message when the response is not 2xx. */
const lanzarSiFallo = async (response: Response, fallback: string) => {
  if (response.ok) return
  const data = await response.json().catch(() => ({}))
  const message =
    typeof data?.error === 'string'
      ? data.error
      : typeof data?.message === 'string'
        ? data.message
        : `Error ${response.status} ${fallback}`
  throw new Error(message)
}

export const afipDelegacionService = {
  /**
   * Validates the request with a synchronous function and, if it answers 200, queues the
   * Background Function that runs Playwright.
   * - `token`: Auth0 JWT from `getAccessTokenSilently`.
   * - `pin`: the user's vault PIN, checked by both functions.
   *
   * Throws when the validation does not answer 200 with `{ ok: true }`.
   * The run's `jobId` is generated here and sent to both functions. The UI polls progress with
   * `listarProgress(jobId)`; the `job` row of that id is the one that ends the run.
   */
  async iniciarAutoDelegacion(
    token: string,
    pin: string,
    input: IniciarAutoDelegacionInput,
  ): Promise<IniciarAutoDelegacionResult> {
    const jobId = crypto.randomUUID()
    const init: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Vault-Pin': pin,
      },
      body: JSON.stringify({ ...input, jobId }),
    }

    const validacion = await fetch(ENDPOINT_VALIDAR_AUTO_DELEGACION, init)
    await lanzarSiFallo(validacion, 'al validar la auto-delegación AFIP')
    const resultado = await validacion.json().catch(() => null)
    if (resultado?.ok !== true) {
      throw new Error('No se pudo validar la auto-delegación AFIP')
    }
    await lanzarSiFallo(
      await fetch(ENDPOINT_AUTO_DELEGAR, init),
      'al iniciar auto-delegación AFIP',
    )

    return {
      status: 'queued',
      message: 'Auto-delegación iniciada. El proceso tarda 1–3 minutos.',
      jobId,
    }
  },

  async abrirAfipDirecto(
    token: string,
    pin: string,
    input: { clienteId: string; organizacionId: string },
  ): Promise<AbrirAfipDirectoResult> {
    const res = await fetch('/.netlify/functions/afip-acceso-directo', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Vault-Pin': pin,
      },
      body: JSON.stringify(input),
    })
    const data = await res.json()
    if (!res.ok) {
      throw Object.assign(new Error(data.error || data.message || 'Error al abrir AFIP'), { status: res.status })
    }
    return data as AbrirAfipDirectoResult
  },

  /**
   * CUIT of the representative that auto-delegation delegates to, read by the server from the studio's ARCA
   * certificate. Throws when the function does not answer 2xx or the answer has no CUIT.
   */
  async obtenerRepresentante(token: string, organizacionId: string): Promise<string> {
    const res = await fetch(ENDPOINT_REPRESENTANTE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ organizacionId }),
    })
    await lanzarSiFallo(res, 'al leer el representante de la auto-delegación')
    const data = await res.json().catch(() => null)
    if (typeof data?.cuit !== 'string') {
      throw new Error('La respuesta del representante de la auto-delegación no trae el CUIT')
    }
    return data.cuit
  },

  async listarServiciosDelegados(
    client: SupabaseClient<Database>,
    clienteId: string,
    organizacionId: string,
  ): Promise<ServicioDelegado[]> {
    const { data, error } = await client
      .from('afip_servicios_delegados')
      .select('*')
      .eq('cliente_id', clienteId)
      .eq('organizacion_id', organizacionId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data ?? []
  },

  async listarProgress(
    client: SupabaseClient<Database>,
    jobId: string,
    organizacionId: string,
  ): Promise<DelegacionProgress[]> {
    const { data, error } = await client
      .from('afip_delegacion_progress')
      .select('*')
      .eq('job_id', jobId)
      .eq('organizacion_id', organizacionId)
      .order('created_at', { ascending: true })

    if (error) throw error
    return data ?? []
  },
}
